import {
    Headset, CalendarCheck, Handshake, PhoneCall, ChatsCircle, Funnel,
    PaperPlaneTilt, MagnifyingGlass, Briefcase, Waveform, Buildings, Golf,
    Barbell, Storefront, Globe, Database, ChartLineUp, ShieldCheck, Wrench,
} from "@phosphor-icons/react";

export const BRAND = {
    name: "HIGHTS",
    tagline: "Your business. Engineered to grow.",
    email: "hightsscales@gmail.com",
    phoneDisplay: "+44 7351 290703",
    phoneIntl: "447351290703",
    whatsappMsg: "Hi HIGHTS, I'd like to find out how you could build a growth system for my business.",
};

export const MARQUEE_ITEMS = ["BUILD", "AUTOMATE", "OPTIMISE", "GROW"];

export const SYSTEM_NODES = [
    { key: "website", label: "WEBSITE", blurb: "Premium, bespoke, interactive and 3D websites — engineered to convert visitors into enquiries, not just to look good." },
    { key: "ai", label: "AI", blurb: "AI employees that answer enquiries, qualify leads, book appointments and support customers — 24 hours a day." },
    { key: "crm", label: "CRM", blurb: "Every enquiry, conversation and booking flows into one pipeline you can actually see and act on." },
    { key: "leads", label: "LEADS", blurb: "Lead capture, lead-finding systems and instant qualification — so no opportunity slips through." },
    { key: "sales", label: "SALES", blurb: "AI sales agents, follow-up sequences and proposal journeys that keep opportunities moving." },
    { key: "bookings", label: "BOOKINGS", blurb: "Appointment setting, calendars, reminders and no-show reduction — fully automated." },
    { key: "seo", label: "SEO", blurb: "Technical SEO, Google Business optimisation and local search built into the foundations of everything we ship." },
    { key: "analytics", label: "ANALYTICS", blurb: "Dashboards that show what is actually happening: visitors, enquiries, bookings, revenue signals." },
    { key: "automation", label: "AUTOMATION", blurb: "Repetitive journeys automated end-to-end — follow-ups, onboarding, reminders, internal admin." },
    { key: "support", label: "SUPPORT", blurb: "AI customer support and reception that answers instantly and escalates to humans when it matters." },
    { key: "payments", label: "PAYMENTS", blurb: "Payment capture woven into the customer journey — deposits, bookings, e-commerce, invoices." },
    { key: "social", label: "SOCIAL", blurb: "Social media systems that feed the same pipeline — content, capture and retargeting connected." },
    { key: "security", label: "SECURITY", blurb: "Cybersecurity, monitoring, backups and performance — the infrastructure that keeps it all running." },
];

export const AGENTS = [
    {
        id: "enquiry", name: "AI Enquiry Agent", icon: ChatsCircle,
        tag: "Answers every website enquiry in seconds, day or night.",
        greeting: "Hello — welcome. I'm the enquiry assistant for this business. How can I help you today?",
        suggestions: [
            { q: "How much does this service cost?", a: "I'd be happy to help with that. Pricing depends on exactly what you're looking to achieve — may I ask what your main goal is, so I can point you to the right option?" },
            { q: "How quickly can you start?", a: "Great question. Timelines depend on the scope of the work, but most projects begin within days of the consultation. Would you like me to arrange one for you?" },
            { q: "What areas do you cover?", a: "We work with businesses of any size and industry, remotely and on-site where needed. Can I ask a little about your business so I can be more specific?" },
        ],
        handoff: "Based on what you've told me, a short consultation would be the best next step. Would you like me to arrange that now?",
    },
    {
        id: "appointment", name: "AI Appointment Setter", icon: CalendarCheck,
        tag: "Fills your calendar with qualified appointments automatically.",
        greeting: "Hi there. I handle bookings and scheduling. Are you looking to arrange a time to speak with the team?",
        suggestions: [
            { q: "Can I book a call for this week?", a: "Of course. There are openings on Thursday at 10:00 and Friday at 14:30 — which suits you better?" },
            { q: "Can I rearrange an existing booking?", a: "No problem at all. I can move that for you — what day works best? I'll confirm the change by email and text automatically." },
            { q: "What happens on the call?", a: "It's a relaxed 10–20 minute conversation about your goals and where automation could help. No obligation. Shall I reserve a slot?" },
        ],
        handoff: "I can confirm that slot right now and send you a calendar invite with a reminder the day before.",
    },
    {
        id: "sales", name: "AI Sales Agent", icon: Handshake,
        tag: "Guides prospects from first question to confident decision.",
        greeting: "Welcome. I can walk you through options, pricing and what results to expect. What are you considering?",
        suggestions: [
            { q: "What's the difference between your packages?", a: "It comes down to how much of the system you want working for you — a website alone, or the full connected setup with AI, CRM and automation. What's your current situation?" },
            { q: "Is this worth it for a small business?", a: "Honestly, smaller businesses often feel the impact fastest — every missed enquiry costs more when the team is small. What's your biggest bottleneck right now?" },
            { q: "Can I see proof this works?", a: "Absolutely — the demonstrations on this page are live, working systems. You're interacting with one right now. Would you like to see what this could look like for your business?" },
        ],
        handoff: "The clearest next step is a short consultation where we map this to your business specifically. Shall I book that for you?",
    },
    {
        id: "receptionist", name: "AI Receptionist", icon: PhoneCall,
        tag: "Answers every call and message professionally — never misses one.",
        greeting: "Good afternoon, thank you for calling. How may I direct your enquiry today?",
        suggestions: [
            { q: "I want to speak to someone about a quote.", a: "Of course. I can take the details now and have the right person call you back within the hour — or I can book you straight into their calendar. Which would you prefer?" },
            { q: "What are your opening hours?", a: "The office is open 9 to 6, but I'm available around the clock — so you'll never reach a voicemail. How can I help?" },
            { q: "Can you take a message?", a: "Certainly. May I take your name, number and the reason for your enquiry? I'll make sure it's logged and passed to the right person immediately." },
        ],
        handoff: "I've logged all of that. Would you like me to confirm a callback time so you're guaranteed a response?",
    },
    {
        id: "support", name: "AI Customer Support", icon: Headset,
        tag: "Resolves routine questions instantly, escalates what matters.",
        greeting: "Hi — I'm the support assistant. I can help with orders, bookings, accounts and common questions. What do you need?",
        suggestions: [
            { q: "Where is my order?", a: "Let me check that for you. In a live deployment I'd pull this directly from your system — for this demonstration: your order is on schedule and due Thursday. Anything else?" },
            { q: "I need to change my booking.", a: "I can handle that. What date would suit you instead? I'll update it everywhere and send confirmation automatically." },
            { q: "This is urgent — can I speak to a human?", a: "Of course. I'm flagging this as priority and connecting you to the team now. You'll also get a text with their direct line." },
        ],
        handoff: "That ticket is logged and tracked. Would you like updates by email or text?",
    },
    {
        id: "qualifier", name: "AI Lead Qualification", icon: Funnel,
        tag: "Scores and routes every lead before it touches your team.",
        greeting: "Thanks for your interest. I ask a few quick questions so the team can give you a genuinely useful response. Ready?",
        suggestions: [
            { q: "Sure, go ahead.", a: "Great. First — what's the main outcome you're hoping for: more enquiries, better conversion, or less manual admin?" },
            { q: "Why do you need my budget?", a: "Fair question. It helps us recommend the right scope — a £2k solution and a £20k system solve different problems. A rough range is plenty." },
            { q: "What happens with my answers?", a: "They go straight into the CRM as a scored, qualified lead — so when you speak to the team, they already understand your situation. No repeating yourself." },
        ],
        handoff: "That's everything I need. You're qualified for a free consultation — shall I book it now?",
    },
    {
        id: "followup", name: "AI Follow-Up Agent", icon: PaperPlaneTilt,
        tag: "Chases every quote, enquiry and no-show so you never have to.",
        greeting: "This is a demonstration of automated follow-up. Imagine a prospect enquired three days ago and went quiet — watch what happens next.",
        suggestions: [
            { q: "Show me a follow-up message.", a: "\"Hi Sarah — just checking you received the quote I sent on Monday? Happy to answer anything. I also have a consultation slot Thursday if that's easier.\" — Sent automatically, in your tone of voice." },
            { q: "What if they don't reply?", a: "The sequence continues politely — a value-add message, then a final check-in, spaced over days. The moment they reply, a human is notified and the automation stops." },
            { q: "Won't this feel like spam?", a: "No — every message references their actual enquiry, goes out at sensible times, and stops the instant they respond. It feels like a diligent assistant, because that's what it is." },
        ],
        handoff: "Follow-up is where most businesses lose the most revenue. Shall we look at your follow-up process on a call?",
    },
    {
        id: "leadfinder", name: "AI Lead Finder", icon: MagnifyingGlass,
        tag: "Finds and approaches ideal prospects before they find you.",
        greeting: "I research and identify prospects that match your ideal customer profile. What kind of customers are you looking for?",
        suggestions: [
            { q: "How do you find leads?", a: "By combining public business data, intent signals and your ideal profile — then verifying contact details before anything reaches your pipeline. What industry are you targeting?" },
            { q: "Is this GDPR compliant?", a: "Yes — we work with legitimate business-contact data sources and honour suppression lists. Compliance is designed in, not bolted on." },
            { q: "Then what happens?", a: "Qualified prospects enter outreach sequences, replies land in your CRM, and interested responses get booked straight into your calendar." },
        ],
        handoff: "Imagine starting each week with warm conversations already booked. Shall we map your ideal customer profile on a call?",
    },
    {
        id: "assistant", name: "AI Business Assistant", icon: Briefcase,
        tag: "Your operations co-pilot — reports, reminders, answers on demand.",
        greeting: "Good morning. Here's your snapshot: 6 new enquiries overnight, 3 appointments booked, 1 review to respond to. What would you like to dig into?",
        suggestions: [
            { q: "Summarise this week's enquiries.", a: "41 enquiries this week, up 18% — most from Google search. Average response time: 42 seconds (all handled automatically). 12 converted to bookings." },
            { q: "Anything needs my attention?", a: "Two things: a high-value lead asked about commercial work, and Sunday's booking has a clash. I've drafted responses for both — want to review them?" },
            { q: "How is the website performing?", a: "Conversion is at 4.1% this month, up from 3.2%. The new service page is already ranking. Full breakdown is on your dashboard." },
        ],
        handoff: "This is what running a business feels like with a connected system. Want to see it built around yours?",
    },
    {
        id: "voice", name: "AI Voice Receptionist", icon: Waveform,
        tag: "A natural voice that answers your phone like your best employee.",
        greeting: "This panel simulates a live phone call with an AI voice receptionist. Ask anything a caller would ask.",
        suggestions: [
            { q: "Do you have any availability tomorrow?", a: "\"Let me check… Yes — we have 11:30 or 16:00 tomorrow. Which works for you? …Perfect, that's booked. You'll get a text confirmation in a moment.\" — Spoken naturally, in a voice that matches your brand." },
            { q: "How much is a callout?", a: "\"Callouts within the local area are quoted free of charge. Can I take your postcode and a brief description, so the engineer comes prepared?\"" },
            { q: "Can I speak to the manager?", a: "\"Of course — the manager is with a customer right now. I'll make sure they call you back within the hour, or I can book a specific time. Which do you prefer?\"" },
        ],
        handoff: "Every call answered, every detail captured, every caller looked after. Shall we set up your voice?",
    },
];

export const SERVICE_CATEGORIES = [
    {
        id: "build", label: "BUILD", icon: Globe,
        items: [
            { label: "Premium websites", slug: "bespoke-websites" },
            { label: "Interactive websites", slug: "interactive-websites" },
            { label: "3D websites", slug: "3d-website-development" },
            { label: "Web applications", slug: "web-applications" },
            { label: "Mobile applications", slug: "mobile-app-development" },
            { label: "E-commerce", slug: "ecommerce-automation" },
        ],
    },
    {
        id: "automate", label: "AUTOMATE", icon: Waveform,
        items: [
            { label: "AI enquiry agents", slug: "ai-enquiry-agent" },
            { label: "AI appointment setters", slug: "ai-appointment-setter" },
            { label: "AI sales agents", slug: "ai-sales-agent" },
            { label: "AI receptionists", slug: "ai-receptionist" },
            { label: "AI customer support", slug: "ai-customer-support" },
            { label: "AI voice systems", slug: "ai-voice-receptionist" },
            { label: "CRM automation", slug: "crm-automation" },
            { label: "Automated follow-up", slug: "business-automation" },
        ],
    },
    {
        id: "grow", label: "GROW", icon: ChartLineUp,
        items: [
            { label: "SEO", slug: "seo-services" },
            { label: "Google optimisation", slug: "google-business-optimisation" },
            { label: "Local SEO", slug: "google-business-optimisation" },
            { label: "Conversion optimisation", slug: "conversion-optimisation" },
            { label: "Lead generation", slug: "lead-generation-automation" },
            { label: "Analytics & dashboards", slug: null },
        ],
    },
    {
        id: "protect", label: "PROTECT", icon: ShieldCheck,
        items: [
            { label: "Cybersecurity", slug: "website-security" },
            { label: "Website security", slug: "website-security" },
            { label: "Monitoring & backups", slug: null },
            { label: "Performance optimisation", slug: "conversion-optimisation" },
        ],
    },
    {
        id: "manage", label: "MANAGE", icon: Wrench,
        items: [
            { label: "Maintenance & updates", slug: null },
            { label: "Hosting", slug: null },
            { label: "Continuous optimisation", slug: "conversion-optimisation" },
            { label: "Growth partnerships", slug: null },
        ],
    },
];

export const STATS = [
    { value: "78%", text: "of customers buy from the first business that responds to their enquiry.", source: "Lead Connect" },
    { value: "7×", text: "more likely to qualify a lead when contacted within one hour, versus even one hour later.", source: "Harvard Business Review — The Short Life of Online Sales Leads" },
    { value: "53%", text: "of mobile visits are abandoned if a page takes longer than 3 seconds to load.", source: "Google / SOASTA Research" },
    { value: "8.4%", text: "increase in retail conversions from just a 0.1 second improvement in mobile site speed.", source: "Deloitte — Milliseconds Make Millions" },
    { value: "28%", text: "of the average sales professional's week is spent actually selling. The rest is admin and process.", source: "Salesforce — State of Sales Report" },
    { value: "68%", text: "of online experiences begin with a search engine.", source: "BrightEdge" },
];

export const WORKFLOW_STEPS = [
    "Enquiry received", "AI qualified", "CRM updated", "Appointment booked", "Follow-up automated",
];

export const DEMOS = [
    {
        id: "property", title: "HIGHTS PROPERTY", type: "Interactive website", icon: Buildings,
        img: "/assets/img/property-1.jpg", href: "/demos/property",
        desc: "A premium estate agency with listings, an AI property assistant, viewing bookings and a fully automated enquiry-to-CRM journey.",
    },
    {
        id: "golf", title: "HIGHTS GOLF", type: "Interactive website", icon: Golf,
        img: "/assets/img/golf-1.jpg", href: "/demos/golf",
        desc: "A luxury golf and hospitality destination — tee times, memberships, packages, an AI concierge and automated guest follow-up.",
    },
    {
        id: "fitness", title: "HIGHTS FITNESS", type: "Mobile application", icon: Barbell,
        img: "/assets/img/fitness.jpg", href: "/demos/app/fitness",
        desc: "A member app with packages, payments, class booking, progress tracking and an AI fitness assistant connected to the CRM.",
    },
    {
        id: "market", title: "HIGHTS MARKET", type: "Mobile application", icon: Storefront,
        img: "/assets/img/ecosystem.jpg", href: "/demos/app/market",
        desc: "A multi-business marketplace — discovery, comparison, AI shopping assistant, basket, checkout and business analytics.",
    },
];

export const REVIEWS_PLACEHOLDER = true;
